import React, { useCallback, useEffect, useState } from 'react';
import { StickyNote, Trash2, Edit2, Check, X } from 'lucide-react';
import { expenseAPI } from '../../services/api';
import { useToast, useConfirm } from '../UIProvider';
import { formatClinicDateTime } from '../../utils/clinicTime';
import { LoadingRow, errorText, inputClass } from './shared';

// A scratchpad for where the bookkeeping stands: "entered up to the WhatsApp messages of 22
// August", "medicines received after 3 September are not in yet". Written by whoever is doing
// it, for whoever picks it up next.

const when = (iso) => (iso ? formatClinicDateTime(iso) : '');

const ExpenseNotes = () => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await expenseAPI.getNotes();
      setNotes(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast('error', errorText(err, 'Failed to load notes'));
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await expenseAPI.createNote(draft.trim());
      setDraft('');
      await load();
      showToast('success', 'Note saved');
    } catch (err) {
      showToast('error', errorText(err, 'Failed to save the note'));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) { showToast('error', 'A note cannot be emptied. Delete it instead.'); return; }
    try {
      await expenseAPI.updateNote(id, editText.trim());
      setEditingId(null);
      await load();
    } catch (err) {
      showToast('error', errorText(err, 'Failed to save the note'));
    }
  };

  const remove = async (note) => {
    const ok = await confirm({ title: 'Delete this note?', message: note.body.slice(0, 120), confirmText: 'Delete', tone: 'danger' });
    if (!ok) return;
    try {
      await expenseAPI.deleteNote(note.id);
      await load();
    } catch (err) {
      showToast('error', errorText(err, 'Failed to delete the note'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <StickyNote className="h-5 w-5 text-emerald-600" /> Notes
        </h3>
        <p className="text-xs text-gray-400 mb-4">Where the bookkeeping stands, for whoever picks it up next.</p>
        <textarea
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. Everything up to the 22 August WhatsApp messages is entered."
          className={`${inputClass} resize-none`}
        />
        <div className="mt-3 flex justify-end">
          <button onClick={add} disabled={saving || !draft.trim()} className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50">
            Save note
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xl">
        {loading ? (
          <LoadingRow text="Loading notes..." />
        ) : notes.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No notes yet</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {notes.map(note => (
              <li key={note.id} className="px-6 py-4">
                {editingId === note.id ? (
                  <div>
                    <textarea
                      rows={3}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className={`${inputClass} resize-none`}
                      autoFocus
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-1"><X className="h-4 w-4" /> Cancel</button>
                      <button onClick={() => saveEdit(note.id)} className="px-3 py-1.5 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 flex items-center gap-1"><Check className="h-4 w-4" /> Save</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{note.body}</p>
                      <p className="text-xs text-gray-400 mt-1.5">
                        {note.createdByName || 'Someone'} · {when(note.createdAt)}
                        {note.updatedAt && ` · edited ${when(note.updatedAt)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => { setEditingId(note.id); setEditText(note.body); }} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" title="Edit"><Edit2 className="h-4 w-4" /></button>
                      <button onClick={() => remove(note)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" title="Delete"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ExpenseNotes;
